import { buttonStyle } from 'components/buttons/buttonStyle';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import { FormattedDate } from 'components/misc/FormattedDate';
import { Button } from 'components/ui/button';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { HistoryForm, HistoryRecordState } from 'types/histories';
import { getClassifierText, t } from 'utils/getTranslations';
import {
    allowsMultipleActiveRecords,
    hasDateOverlapWithSiblings,
    isDateEarlierThan,
    isDateLaterThan,
} from 'utils/validators';

interface Props {
    index: number;
    history: HistoryForm;
    allClassifiers: ClassifierData[];
    classifierCode: string | null;
    hasUserEditPermission: boolean;
    recordState: HistoryRecordState;
    isLatestRecord: boolean;
    updateRecordState: () => void;
    handleSaveHistoryRecord: (data: Pick<HistoryForm, 'startDate' | 'endDate'>) => Promise<void>;
    handleDeleteHistoryRecord?: () => Promise<void>;
    allHistories: HistoryForm[];
}

const HistoryRecordContent: FC<Props> = ({
    history,
    allClassifiers,
    classifierCode,
    hasUserEditPermission,
    recordState,
    isLatestRecord,
    updateRecordState,
    handleSaveHistoryRecord,
    handleDeleteHistoryRecord,
    allHistories,
}) => {
    const { control, handleSubmit, formState } = useForm<Pick<HistoryForm, 'startDate' | 'endDate'>>({
        mode: 'onChange',
        values: {
            startDate: history.startDate,
            endDate: history.endDate,
        },
    });
    const displayedValue =
        classifierCode !== null
            ? getClassifierText(
                  allClassifiers.find((classifier) => classifier.code === classifierCode),
                  history.fieldValue
              )
            : history.fieldValue;

    return (
        <div className="flex w-full flex-row items-start gap-3 px-4">
            <span className="w-[40%]">{displayedValue || '-'}</span>
            <div className="w-[26%]">
                {recordState === HistoryRecordState.VIEWING ? (
                    <span>
                        <FormattedDate value={history.startDate} />
                    </span>
                ) : (
                    <DateInput
                        gap={0}
                        fullWidth
                        control={control}
                        name="startDate"
                        required
                        validate={{
                            isEarlierThan: (_, formValues) => {
                                return isDateEarlierThan(
                                    formValues.startDate ?? '',
                                    formValues.endDate ?? '',
                                    t('history.table-header.end-date')
                                );
                            },
                            noOverlap: (_, formValues) => {
                                return hasDateOverlapWithSiblings(
                                    formValues.startDate,
                                    formValues.endDate,
                                    history.id,
                                    history.fieldType,
                                    allHistories
                                );
                            },
                        }}
                    />
                )}
            </div>
            <div className="w-[26%] justify-center">
                {recordState === HistoryRecordState.VIEWING ? (
                    <span>
                        <FormattedDate value={history.endDate} />
                    </span>
                ) : (
                    <DateInput
                        gap={0}
                        fullWidth
                        control={control}
                        name="endDate"
                        required={!isLatestRecord && !allowsMultipleActiveRecords(history.fieldType)}
                        validate={{
                            isLaterThan: (_, formValues) => {
                                return isDateLaterThan(
                                    formValues.endDate ?? '',
                                    formValues.startDate ?? '',
                                    t('history.table-header.start-date')
                                );
                            },
                            noOverlap: (_, formValues) => {
                                return hasDateOverlapWithSiblings(
                                    formValues.startDate,
                                    formValues.endDate,
                                    history.id,
                                    history.fieldType,
                                    allHistories
                                );
                            },
                        }}
                    />
                )}
            </div>
            {hasUserEditPermission && (
                <div className="mt-0.5 flex flex-row justify-center gap-2">
                    {recordState === HistoryRecordState.VIEWING ? (
                        <Button
                            key={`${history.id}startEditing`}
                            {...buttonStyle({ variant: 'outlined', size: 'small' }, 'h-[80%]')}
                            onClick={updateRecordState}
                        >
                            <FormattedMessage id="edit" />
                        </Button>
                    ) : (
                        <>
                            {handleDeleteHistoryRecord ? (
                                <Button
                                    key={`${history.id}delete`}
                                    {...buttonStyle({ variant: 'contained', size: 'small', color: 'error' }, 'h-[80%]')}
                                    onClick={() => {
                                        void handleDeleteHistoryRecord();
                                    }}
                                >
                                    <FormattedMessage id="delete" />
                                </Button>
                            ) : null}
                            <Button
                                key={`${history.id}save`}
                                {...buttonStyle({ variant: 'outlined', size: 'small' }, 'h-[80%]')}
                                onClick={() => {
                                    void handleSubmit(handleSaveHistoryRecord)();
                                }}
                                disabled={!formState.isValid}
                            >
                                <FormattedMessage id="save" />
                            </Button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default HistoryRecordContent;
